import 'chartjs-adapter-date-fns';

import { useCallback } from 'react';
import Stack from '@mui/material/Stack';
import { CategoryScale, Chart as ChartJS, LinearScale, LineElement, PointElement, TimeScale, Tooltip } from 'chart.js';
import { format } from 'date-fns';
import { isNil } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { Line } from 'react-chartjs-2';
import { useDispatch, useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader, DaysIndicator, SidebarInfo, SidebarInfoColumn } from 'components';
import Sidebar from 'components/atoms/GefMui/Sidebar/Sidebar';
import SidebarSection from 'components/atoms/GefMui/SidebarSection';
import { campaignTypes } from 'constants/campaigns';
import { defaultDateFormatDateFns, USER_ROLES } from 'constants/general';
import dashboardSidebar from 'features/views/campaignDashboards/sidebar';
import dashboardStructure from 'features/views/campaignDashboards/structure';
import user from 'features/views/user';
import { currency, number, percentage } from 'utils';

import { generateChart } from './SidebarChartOptions';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, TimeScale);

const { views } = dashboardStructure.constants;

export const CampaignsSidebar = () => {
	const dispatch = useDispatch();

	const contextUser = useSelector(dashboardStructure.selectors.getContextUser);
	const currentRole = useSelector(user.selectors.getCurrentRole);
	const view = useSelector(dashboardStructure.selectors.getView);
	const lastRequest = useSelector(dashboardSidebar.selectors.getLastSummaryRequest);

	const campaignSummary = useSelector(dashboardSidebar.selectors.getSummary);

	const {
		production,
		actualReplacement,
		productionPlan,
		planFulfillment,
		addMatFulfillment,
		expectedReplacement,
		campaignProgress,
		endDate,
		startDate,
		timeTillEndOfCampaign,
		campaignType,
		toAssign,
		toRelease,
	} = campaignSummary || {};

	const handleSummaryReload = useCallback(() => {
		dispatch(dashboardSidebar.actions.reloadSummary());
	}, [dispatch]);

	const checkIfShowToAssign = (view, role) => {
		switch (view) {
			case views.OVERVIEW:
				return ![USER_ROLES.REGION_MANAGER, USER_ROLES.SELLER, USER_ROLES.PPZ].includes(role);
			case views.CENTRAL:
			case views.REGION:
			case views.MANAGER:
				return true;
			default:
				return false;
		}
	};

	const checkIfShowToRelease = (view, role) => {
		switch (view) {
			case views.OVERVIEW:
				return ![USER_ROLES.SELLER, USER_ROLES.PPZ].includes(role);
			case views.CENTRAL:
			case views.REGION:
			case views.MANAGER:
				return true;
			default:
				return false;
		}
	};

	return (
		<Sidebar contextUser={contextUser}>
			<ComponentLoader fetchStorePath={lastRequest ? lastRequest.url : ''} onReload={handleSummaryReload}>
				<Sidebar.Body spacing={2} sx={{ p: 3, maxHeight: 'unset !important' }}>
					<SidebarSection heading={getLabelMessage('campaigns:sidebarTitle')}>
						<SidebarInfo>
							<SidebarInfoColumn
								attribute={getLabelMessage('campaigns:currentProduction')}
								value={currency(production)}
							/>
							{campaignType === campaignTypes.UP_SELL && (
								<SidebarInfoColumn
									attribute={getLabelMessage('campaigns:actualReplacement')}
									value={percentage(actualReplacement)}
								/>
							)}
							<SidebarInfoColumn
								attribute={getLabelMessage('campaigns:planFulfillment')}
								value={percentage(planFulfillment)}
							/>
							<SidebarInfoColumn
								attribute={getLabelMessage('campaigns:addMatFulfillment')}
								value={percentage(addMatFulfillment)}
							/>
							<SidebarInfoColumn
								attribute={getLabelMessage('campaigns:productionPlan')}
								value={currency(productionPlan)}
							/>
							{campaignType === campaignTypes.UP_SELL && (
								<SidebarInfoColumn
									attribute={getLabelMessage('campaigns:expectedReplacement')}
									value={percentage(expectedReplacement)}
								/>
							)}
							{checkIfShowToAssign(view, currentRole) && (
								<SidebarInfoColumn
									attribute={getLabelMessage('campaigns:toAssign')}
									value={number(toAssign)}
								/>
							)}
							{checkIfShowToRelease(view, currentRole) && (
								<SidebarInfoColumn
									attribute={getLabelMessage('campaigns:toRelease')}
									value={number(toRelease)}
								/>
							)}
						</SidebarInfo>
					</SidebarSection>
					{campaignSummary && !isNilOrEmpty(campaignSummary.productionsProgression) && (
						<SidebarSection heading={getLabelMessage('campaigns:todaysProduction')}>
							<Stack spacing={1}>
								{format(new Date(), defaultDateFormatDateFns)}
								<div className="TodaysProductionLineGraph">
									{campaignSummary && campaignSummary.productionsProgression && (
										<Line
											{...generateChart(
												campaignSummary.productionsProgression,
												startDate,
												endDate,
												productionPlan,
											)}
										/>
									)}
								</div>
							</Stack>
						</SidebarSection>
					)}
					{!isNil(campaignProgress) && !isNil(timeTillEndOfCampaign) && (
						<SidebarSection heading={getLabelMessage('campaigns:timeToEndCampaign')}>
							<DaysIndicator
								text={`${getLabelMessage('common:day', {
									count: timeTillEndOfCampaign,
								})}`}
								scale={campaignProgress}
							/>
						</SidebarSection>
					)}
				</Sidebar.Body>
			</ComponentLoader>
		</Sidebar>
	);
};

export default CampaignsSidebar;
